/**
 * Browser detection and feature detection utilities.
 *
 * Provides consistent browser/platform detection across the application
 * for handling browser-specific quirks and features.
 *
 * Note: Feature detection is preferred over browser detection when possible.
 * Use these utilities only when browser-specific workarounds are necessary.
 */

/**
 * Check if running in Safari browser (desktop or mobile).
 * Excludes Chrome and other Chromium-based browsers on macOS.
 */
export function isSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|Edge|Firefox|Opera/.test(ua);
}

/**
 * Check if running on iOS (iPhone, iPad, iPod).
 * Also detects iPad on iOS 13+ which reports as MacIntel.
 */
export function isIOS(): boolean {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Check if running on Android.
 */
export function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

/**
 * Check if running on a mobile device (iOS or Android).
 */
export function isMobile(): boolean {
  return isIOS() || isAndroid();
}

/**
 * Check if running in a mobile WebKit browser (iOS Safari, Android WebView).
 */
export function isMobileWebKit(): boolean {
  return /(iPhone|iPad|iPod|Android).*WebKit/.test(navigator.userAgent);
}

/**
 * Get Safari version number (major version only).
 * Returns 0 if not Safari or version cannot be determined.
 */
export function getSafariVersion(): number {
  if (!isSafari()) return 0;

  const match = navigator.userAgent.match(/Version\/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Get iOS version number (major version only).
 * Returns 0 if not iOS or version cannot be determined.
 */
export function getIOSVersion(): number {
  if (!isIOS()) return 0;

  const match = navigator.userAgent.match(/OS (\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Check if the browser supports a specific MIME type for MediaRecorder.
 * Returns the first supported MIME type from the list, or empty string if none.
 *
 * @param mimeTypes - Array of MIME types to check in order of preference
 */
export function getSupportedMediaRecorderMimeType(mimeTypes: string[]): string {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return '';
}

/**
 * Get the best supported audio MIME type for MediaRecorder.
 * Prioritizes MP4/AAC for Safari (WebM not supported), WebM/Opus for others.
 */
export function getBestAudioMimeType(): string {
  // Prioritize MP4 for Safari/iOS, as WebM is not supported for recording
  const mimeTypes =
    isSafari() || isIOS()
      ? [
          'audio/mp4', // Safari primary (AAC codec)
          'audio/aac', // Safari fallback
          'audio/wav', // Universal fallback
          'audio/webm;codecs=opus', // For completeness, though unlikely to work
          'audio/webm',
        ]
      : [
          'audio/webm;codecs=opus', // Chrome, Firefox, Edge primary
          'audio/webm', // Fallback WebM
          'audio/ogg;codecs=vorbis', // Firefox fallback
          'audio/mp4', // Fallback for other cases
          'audio/wav', // Universal fallback
        ];

  return getSupportedMediaRecorderMimeType(mimeTypes);
}

/**
 * Check if the browser supports the Permissions API for a specific permission.
 *
 * @param permissionName - The permission to check (e.g., 'microphone', 'camera')
 */
export async function supportsPermissionQuery(permissionName: string): Promise<boolean> {
  if (!navigator.permissions?.query) {
    return false;
  }

  try {
    await navigator.permissions.query({ name: permissionName as PermissionName });
    return true;
  } catch {
    // Permission not supported for querying (e.g., iOS Safari)
    return false;
  }
}

/**
 * Check if the browser supports backdrop-filter CSS.
 * Safari < 14 requires -webkit-backdrop-filter.
 */
export function supportsBackdropFilter(): boolean {
  return (
    CSS.supports('backdrop-filter', 'blur(1px)') ||
    CSS.supports('-webkit-backdrop-filter', 'blur(1px)')
  );
}

/**
 * Check if the browser supports dynamic viewport units (dvh, svh, lvh).
 * Safari 15.4+ supports these.
 */
export function supportsDynamicViewport(): boolean {
  return CSS.supports('height', '1dvh');
}

/**
 * Check if the browser supports the modern MediaQueryList.addEventListener API.
 * Safari < 14 only supports the deprecated addListener method.
 */
export function supportsMediaQueryListAddEventListener(): boolean {
  if (typeof window.matchMedia !== 'function') {
    return false;
  }

  const mql = window.matchMedia('(min-width: 1px)');
  return typeof mql.addEventListener === 'function';
}

/**
 * Check if the browser supports passive event listeners.
 */
export function supportsPassiveEventListeners(): boolean {
  let supportsPassive = false;

  try {
    const options = {
      get passive() {
        supportsPassive = true;
        return true;
      },
    };

    // Test with a no-op listener
    const noop = () => {};
    window.addEventListener('test', noop, options as AddEventListenerOptions);
    window.removeEventListener('test', noop);
  } catch {
    supportsPassive = false;
  }

  return supportsPassive;
}

/**
 * Get passive event listener options (with fallback for old browsers).
 *
 * @param passive - Whether the listener should be passive
 */
export function getPassiveOptions(passive: boolean): AddEventListenerOptions | boolean {
  if (supportsPassiveEventListeners()) {
    return { passive };
  }
  return false;
}

/**
 * Check if the browser supports the visualViewport API.
 * Useful for handling iOS keyboard viewport changes.
 */
export function supportsVisualViewport(): boolean {
  return typeof window.visualViewport !== 'undefined';
}

/**
 * Check if we're running in a PWA (standalone mode).
 */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
